import { DataService } from './../../../services/data.service';
import { Component, Input, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthService } from '../../../auth/auth.service';
import { Output, EventEmitter } from '@angular/core';
import { BuildType } from 'src/app/enums/build-type';
import { AssetTypes } from 'src/app/entities/enums/AssetTypes';
import { LoggerService } from '../../services/logger.service';
import { FormService } from 'src/app/services/form.service';
import { MiscImageService } from 'src/app/entities/misc-image/services/misc-image.service';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';

// Constants for user data fallback values
const DEFAULT_USER_NAME = 'Unknown';
const DEFAULT_USER_EMAIL = 'unknown@example.com';
const DEFAULT_USER_ID = '';
const DEFAULT_ENVIRONMENT = 'unknown';
const EDIT_SOURCE_BUILD_ASSET = 'build-asset';

// Valid environment values
const VALID_ENVIRONMENTS = ['dev', 'test', 'qa', 'prod', 'development', 'production'];

// Route prefixes of the entities that own an asset build record, used to link a
// duplicate warning to the previous entity instead of its asset record.
const DUP_ASSET_TYPE_ROUTES: { [key: number]: string } = {
  [AssetTypes.Item]: 'items',
  [AssetTypes.ItemThumbnail]: 'items',
  [AssetTypes.Challenge]: 'challenges',
  [AssetTypes.ChallengeFeed]: 'challenges',
  [AssetTypes.LoadingScreen]: 'loading-screens',
};

const DUP_ENTITY_TYPE_ROUTES: { [key: number]: string } = {
  [BuildType.Items]: 'items',
  [BuildType.Levels]: 'challenges',
};

interface UserPayload {
  email: string;
  name: string;
  id: string;
}

interface UserData {
  name: string;
  email: string;
  id: string;
  timestamp: Date;
  environment: string;
  editSource: string;
}


@Component({
  selector: 'build-asset',
  templateUrl: './build-asset.component.html',
  styleUrls: ['./build-asset.component.sass'],
  providers: [ConfirmationService]
})
export class BuildAssetComponent implements OnInit
{
  @Input() entity: any;
  @Input() btnDisabled: boolean = false;
  @Input() path: string = '';
  @Input() path_ref: any;
  @Input() type: string;
  @Input() label: string;
  @Input() buildType: BuildType;
  @Input() isHighPriority: boolean = false;
  @Input() entityBuildDataType: string;
  @Input() createMiscRecord: boolean = false;
  @Input() skipBuild: boolean = false;
  @Input() entityIndex: number;
  @Input() renderAsset: boolean = false;
  @Input() skipMiscValidation: boolean = false;
  @Output() triggerCompleteEvent = new EventEmitter<boolean>();
  recordId: any;
  currentUser: any;
  status: string;
  disableButton: boolean = false;
  miscSubmitUrl: string = 'miscellaneous-build/add';
  approvedAssetsPrefix: string = 'approved_assets/';
  disabledToolTipMessage: string | null = null; 

  constructor(
    private dataService: DataService,
    private messageService: MessageService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private loggerService: LoggerService,
    private formService: FormService,
    private miscImageService: MiscImageService,
  ) {}

  /**
   * Build Asset Component Initialization
   */
  ngOnInit(): void
  {
    const user = this.authService.getSocialUser();
    this.currentUser = user?.currentUser || null;
    
    if(this.entity.entityType == BuildType.Minigames){
      this.buildType = BuildType.Minigames;
    }

    // Check original environment.env for test check to preserve backward compatibility
    // Use loose equality to match original behavior
    if(environment.env == "test"){
      this.disableButton = true;
      this.disabledToolTipMessage = "Build actions disabled in test environment";
    }
  }

  /**
   * Validates and returns a valid environment value.
   * Ensures environment handling follows consistent conventions.
   * 
   * @returns Validated environment string
   */
  private getValidatedEnvironment(): string {
    const env = environment.env;
    
    if (!env || typeof env !== 'string') {
      this.loggerService.log('Warning: environment.env is invalid, using default');
      return DEFAULT_ENVIRONMENT;
    }

    // Normalize common variations
    const normalizedEnv = env.toLowerCase().trim();
    
    // Map variations to standard values
    if (normalizedEnv === 'development' || normalizedEnv === 'dev') {
      return 'dev';
    }
    if (normalizedEnv === 'production' || normalizedEnv === 'prod') {
      return 'prod';
    }
    if (VALID_ENVIRONMENTS.includes(normalizedEnv)) {
      return normalizedEnv;
    }

    this.loggerService.log(`Warning: Unexpected environment value "${env}", using default`);
    return DEFAULT_ENVIRONMENT;
  }

  /**
   * Creates userPayload object for legacy user field.
   * Used for backward compatibility with existing API contracts.
   * 
   * @returns UserPayload object with user information
   */
  private createUserPayload(): UserPayload {
    return {
      email: this.currentUser?.email || DEFAULT_USER_EMAIL,
      name: this.currentUser?.name || DEFAULT_USER_NAME,
      id: this.currentUser?.id || DEFAULT_USER_ID
    };
  }

  /**
   * Creates userData object following the codebase pattern.
   * Centralized method to reduce code duplication and ensure consistency.
   * Validates currentUser structure before accessing properties.
   * 
   * @returns UserData object with user information and metadata
   */
  private createUserData(): UserData {
    // Validate currentUser structure
    if (!this.currentUser || typeof this.currentUser !== 'object') {
      this.loggerService.log('Warning: currentUser is invalid, using fallback values');
      return {
        name: DEFAULT_USER_NAME,
        email: DEFAULT_USER_EMAIL,
        id: DEFAULT_USER_ID,
        timestamp: new Date(),
        environment: this.getValidatedEnvironment(),
        editSource: EDIT_SOURCE_BUILD_ASSET
      };
    }

    return {
      name: this.currentUser.name || DEFAULT_USER_NAME,
      email: this.currentUser.email || DEFAULT_USER_EMAIL,
      id: this.currentUser.id || DEFAULT_USER_ID,
      timestamp: new Date(),
      environment: this.getValidatedEnvironment(),
      editSource: EDIT_SOURCE_BUILD_ASSET
    };
  }

  /**
   * Sets the path for the build.
   */
  setPath() {
    if (this.type != 'miscellaneous-build' && this.path_ref && this.path_ref.id && this.path_ref.path) {
      this.recordId = this.path_ref.id;
      this.path = this.path_ref.path;
    } else if(this.type == 'miscellaneous-build') {
      this.recordId = this.entity.id;
    }
  }

  /**
   * Confirm Build Queue action.
   * If the build is high priority, then the confirmation
   * modal will display. If not, the build will be send to
   * the build queue.
   *
   * @param event Event comming from button action.
   */
  async confirmBuildQueue(event: any) {
    let result;
    if(this.buildType == BuildType.Levels){
      result = await this.checkChallengeStatusAndConfirm();
    } else {
      result = true;
    }

    if (result) {
      this.disableButton = true;
      if (this.isHighPriority) {
        await this.displayConfirmHighPriorityModal();
      } else {
        await this.sentToBuildQueue();
      }
    }
  }


  /**
   * Display the confirm high priority build
   */
  async displayConfirmHighPriorityModal()
  {
    this.confirmationService.confirm(
    {
      message: 'Are you sure that you want to send this build as high priority?',
      header: 'High Priority Build',
      icon: 'pi pi-exclamation-triangle',
      accept: async () =>
      {
        setTimeout(async () => await this.sentToBuildQueue(), 500);  // 500ms delay

      },
      reject: () =>
      {
        setTimeout(() => {
          this.disableButton = false;
          return;
        }, 500);  // 500ms delay
      }
    });
  }

  /**
   * Resolves the owning entity of a duplicated asset record.
   * Asset type is the most specific signal: legacy image records are stored with
   * entityType Images even when they belong to an item or a challenge.
   *
   * @param record Miscellaneous build record returned by the duplicate check.
   * @returns The ID to display and the URL it should link to.
   */
  private getDupRecordLink(record: any): { id: any, url: string }
  {
    const route = DUP_ASSET_TYPE_ROUTES[record?.assetType] || DUP_ENTITY_TYPE_ROUTES[record?.entityType];

    // Records without an owning entity (standalone misc images, nurture/minigame
    // assets) keep pointing at the asset record itself.
    if (!route || !record?.assetReferenceId) {
      return { id: record?.id, url: `/miscellaneous-build/${record?.id}` };
    }

    return { id: record.assetReferenceId, url: `/${route}/${record.assetReferenceId}` };
  }

  /**
   *
   */
  async displayConfirmBuildDup(messageData: any)
  {
    // console.log(messageData);
    // List the previous entity IDs (e.g. the challenge the duplicate was copied from)
    // rather than the asset build record numbers.
    let listItems = messageData.DupID.map((obj: any) => {
      let link = this.getDupRecordLink(obj);
      return `<li><a href="${link.url}" target="_blank" class="">${link.id}</a></li>`;
    }).join('');

    this.confirmationService.confirm(
    {
      message: `
      This ${this.type}
      ${messageData.AssetReferenceId ? `ID (${this.entity.id})`: `with asset record ID (${messageData.EntityId})`},
      and the following ${this.type} with IDs have the same path:
      <ul>${listItems}</ul>
      <br>
      Do you still want to build?
        `,
      header: 'Building duplicated build record',
      icon: 'pi pi-exclamation-triangle',
      accept: async () =>
      {
        await this.sentToBuildQueue(true);
      },
      reject: () =>
      {
        this.disableButton = false;
        return;
      }
    });
  }


  /**
   * Send the build job request to build queue
   */
  async sentToBuildQueue(skipDupCheck = false): Promise<boolean>
  {
    try {
      if (this.entity.isMultiple) {
        const userData = this.createUserData();

        const bulkPayload = {
          entityType: this.entity.entityType,
          assetType: this.entity.assetType,
          isPriority: this.isHighPriority,
          skipBuild: this.skipBuild,
          userData: userData,
          assets: this.entity.assets.map((a: any) => ({
            name: a.name,
            path: a.path,
            localized: this.entity.localized ?? false,
          })),
        };

        try {
          const response: any = await this.dataService.post('/api/miscellaneous-build/bulk/add', bulkPayload);
          if (response && response.Success) {
            this.showAlert({
              severity: 'success',
              summary: 'Bulk Build Queued',
              detail: `${bulkPayload.assets.length} assets sent to build queue.`,
            });
            this.triggerCompleteEvent.emit(true);
          } else {
            const msg = (response as any).Message || 'Unknown error';
            this.showAlert({
              severity: 'error',
              summary: 'Error Queuing Bulk Build',
              detail: msg,
            });
          }
        } catch (error: any) {
          const errorDetail = error?.response?.error?.errors 
            ? JSON.stringify(error.response.error.errors) 
            : (error?.response?.error?.message || error?.message || 'Unexpected error');
          
          // Log error without sensitive user information
          this.loggerService.log('Error queuing bulk build', {
            entityType: this.entity.entityType,
            assetCount: bulkPayload.assets.length,
            error: errorDetail
          });

          this.showAlert({
            severity: 'error',
            summary: 'Bulk Build Error',
            detail: errorDetail,
          });
        }
      } else {
        this.setPath();
        const userPayload = this.createUserPayload();
        const userData = this.createUserData();

        var payload =
        {
          path: this.path,
          recursive: true,
          buildType: this.buildType,
          entityId: this.recordId,
          user: userPayload,
          userData: userData,
          isPriority: null,
          entityBuildDataType: this.entityBuildDataType,
          isFirstBuild: (this.entity.buildData && this.entity.buildData.length > 0) || (this.entity.assetBuildOutput && Object.keys(this.entity.assetBuildOutput).length > 0) ? false : true,
          entityIndex: this.entityIndex,
          renderAsset: this.renderAsset,
          parentRecordId: this.entity.id,
          skipDupCheck: skipDupCheck,
          entityType: this.type != 'miscellaneous-build' && this.path_ref?.entityType ? this.path_ref.entityType : this.entity.entityType,
          entity: this.type != 'miscellaneous-build' && this.path_ref ? this.path_ref : this.entity,
          localized: this.entity.localized ? true : false,
          assetType: 0,
          generateStoreThumb: this.entity.generateStoreThumb ? true : false
        };
        
        let assetTypeValue;
        const pathRefAssetType = this.type != 'miscellaneous-build' && this.path_ref?.assetType ? this.path_ref.assetType : this.entity.assetType;
        
        if (typeof pathRefAssetType === 'number') {
          assetTypeValue = pathRefAssetType;
        } else if (this.type === 'item-thumbnail') {
          assetTypeValue = 4; 
        } else if (this.type === 'items') {
          assetTypeValue = 0; 
        } else {
          assetTypeValue = -2;
        }
        
        payload.assetType = assetTypeValue;

        if(this.type == 'miscellaneous-build' && this.createMiscRecord)
        {
          let miscPath = this.path.replace("approved_assets/","")
          let response = await this.miscImageService.validateMiscRecordPath(miscPath);
          if(this.skipMiscValidation || (response.Success && response.IsOk))
          {
            payload.entityId = await this.getMiscBuildId();
          }
          else
          {
            this.showAlert
            (
              {
                severity: 'error',
                summary: 'Error Queuing Build',
                detail: `${miscPath} path already exist. Expected Path to be unique.`,
              }
            );
            this.disableButton = false;
            return false;
          }
        }

        // Log payload without sensitive user information
        const logPayload = {
          ...payload,
          user: { ...payload.user, id: '***' }, // Mask user ID in logs
          userData: payload.userData ? { ...payload.userData, id: '***', email: '***' } : null
        };
        this.loggerService.log('payload from build button', logPayload);
        
        if((payload.path && payload.path.length > 0 && payload.user))
        {
          if(this.validatePath() && !this.skipBuild)
          {
            try
            {
              const response = await this.dataService.sendToBuildQueue(this.buildType, payload, this.isHighPriority);
              this.loggerService.log("SendToBuildQueue - Response: ", response);
              if(response && response.Success)
              {
                let detail = this.isHighPriority ? `Sent to build queue as high priority.` : `Sent to build queue.`;
                this.showAlert(
                {
                  severity: 'success',
                  summary: 'Build Queued',
                  detail: detail,
                });
                this.triggerCompleteEvent.emit(true);
              }
              else
              {
                if(response.Type && response.Type == "originalDuplicate"){
                  await this.displayConfirmBuildDup(response);
                }
                this.showAlert(
                {
                  severity: 'error',
                  summary: 'Error Queuing Build',
                  detail: `${response.Message}`,
                });
              }
            }
            catch (error: any)
            {
              const errorMessage = error?.message || 'There was an error sending the build to the queue';
              
              // Log error without sensitive information
              this.loggerService.log('Error sending build to queue', {
                entityId: payload.entityId,
                buildType: payload.buildType,
                error: errorMessage
              });

              this.showAlert({
                severity: 'error',
                summary: 'Error Queuing Build',
                detail: `${errorMessage}. Please try again.`,
              });
            }
          } else {
            this.showAlert(
              {
                severity: 'success',
                summary: 'Asset Record Created',
                detail: `Created Asset record ${ payload.entityId?payload.entityId : null} and skipped Build`,
              });
              this.triggerCompleteEvent.emit(true);
          }
        }
        else
        {
          this.showAlert(
            {
              severity: 'error',
              summary: 'Error Queuing Build',
              detail: `Path or user not specified. Payload: ${JSON.stringify(payload)}`,
            });
        }
      }
      return true;
    } finally {
      this.disableButton = false;
    }
  }

  /**
   * Create build payload for a single asset.
   * Uses centralized userData creation to ensure consistency.
   * 
   * @param options Configuration options for the build payload
   * @returns Build payload object with userData included
   */
  private createBuildPayload(options: {
    path: string,
    entityId: any,
    parentRecordId: any,
    skipDupCheck: boolean,
    name: string
  }) {
    const userPayload = this.createUserPayload();
    const userData = this.createUserData();

    let assetTypeValue;
    const pathRefAssetType = this.type !== 'miscellaneous-build' && this.path_ref?.assetType ? this.path_ref.assetType : this.entity.assetType;
    
    if (typeof pathRefAssetType === 'number') {
      assetTypeValue = pathRefAssetType;
    } else if (this.type === 'item-thumbnail') {
      assetTypeValue = 4; 
    } else if (this.type === 'items') {
      assetTypeValue = 0; 
    } else {
      assetTypeValue = -2;
    }

    return {
      path: options.path,
      recursive: true,
      buildType: this.buildType,
      entityId: options.entityId,
      user: userPayload,
      userData: userData,
      isPriority: null,
      entityBuildDataType: this.entityBuildDataType,
      isFirstBuild: true,
      entityIndex: this.entityIndex,
      renderAsset: this.renderAsset,
      parentRecordId: options.parentRecordId,
      skipDupCheck: options.skipDupCheck,
      entityType: this.entity.entityType,
      assetType: assetTypeValue,
      entity: {
        name: options.name,
        entityType: this.entity.entityType,
        assetType: assetTypeValue
      },
      localized: this.entity.localized || false
    };
  }

  /**
   * Process a single build request
   */
  private async processBuildRequest(payload: any) {
    try {
      let response = await this.dataService.sendToBuildQueue(this.buildType, payload, this.isHighPriority);
      if (response && response.Success) {
        let detail = this.isHighPriority ? 'Sent to build queue as high priority.' : 'Sent to build queue.';
        this.showAlert({
          severity: 'success',
          summary: 'Build Queued',
          detail: detail,
        });
      } else {
        if (response.Type && response.Type == "originalDuplicate") {
          await this.displayConfirmBuildDup(response);
        } else {
          this.showAlert({
            severity: 'error',
            summary: 'Error Queuing Build',
            detail: `${response.Message}`,
          });
        }
      }
    } catch (error) {
      this.showAlert({
        severity: 'error',
        summary: 'Error Queuing Build',
        detail: 'There was an error sending the build to the queue. Please try again.',
      });
    }
  }

  /**
   * Modified getMiscBuildId to handle single asset data
   */
  async getMiscBuildId(asset?: any) {
    let newMiscRecord: any = {
      path: (asset?.path || this.path).replace("approved_assets/", ""),
      name: asset?.name || this.entity.name,
      enabled: true,
      entityType: this.entity.entityType,
      assetType: this.entity.assetType,
      localized: this.entity.localized
    };

    if (this.entity.themeAdditive_ref && this.entity.themeAdditive_ref.length > 0) {
      newMiscRecord.themeAdditive_ref = this.entity.themeAdditive_ref.map((item: any) => item._id || item);
    }
    
    try {
      const val = await firstValueFrom(this.formService.submitForm(newMiscRecord, this.miscSubmitUrl, false));
      return val.id;
    } catch (response: any) {
      let errorMessage = response.error?.error?.message ? response.error.error.message : response.error?.message;
      let errorName = response.error?.error?.name ? response.error.error.name : response.error?.error;
      this.loggerService.log('POST call in error', response);

      this.showAlert({
        severity: 'error',
        summary: errorName,
        detail: errorMessage,
      });
      this.disableButton = false;
      throw response;
    }
  }

  /**
   * Display alert to show message to the user.
   *
   * @param message Message to show to the user when the action was executed.
   */
  showAlert(message: any)
  {
    this.messageService.add(message);
  }

  /**
   * Check if the path is valid to build
   */
  validatePath()
  {
    let pathType = "";
    let fileNameValue = "";
    let isValid = true;

    if(this.type == 'items' || this.type == 'challenges')
    {
      pathType = this.type == 'items' ? "Prefab" : "Scene";

      let fullPath = this.type == 'items' ? this.entity.prefab.split('/') : this.entity.scene.split('/');
      let fileName = fullPath[fullPath.length - 1];
      let path = fullPath[fullPath.length - 2];
      fileNameValue = fileName;

      isValid = fileName == path;

      if(!isValid)
      {
        this.showAlert
        (
          {
            severity: 'error',
            summary: 'Error Queuing Build',
            detail: `${pathType} path error. Filename (${fileNameValue}) mismatch within the path.`,
          }
        );
      }
    }

    return isValid;
  }

  validateChallengeLiveRange() {
    const start = this.entity?.start;
    const end = this.entity?.end;

    // Create voteEnd Date which is one day after the end date
    const voteStart = new Date(end);
    const voteEnd = new Date(voteStart);
    voteEnd.setDate(voteEnd.getDate() + 1);

    // Get current time in San Francisco timezone as a string
    const sfTimeString = new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" });

    // Convert the string back to a Date object for easier comparison
    const sfTime = new Date(sfTimeString);

    // Validate if the challenge is currently live or in voting period
    const isLive = sfTime >= new Date(start) && sfTime <= new Date(end);
    const isVoting = sfTime <= new Date(voteEnd) && sfTime > new Date(end);

    return { isLive, isVoting };
  }

  async checkChallengeStatusAndConfirm(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const { isLive, isVoting } = this.validateChallengeLiveRange();

      if (isLive || isVoting) {
        this.confirmationService.confirm({
          message: `The challenge is currently ${isLive ? 'live' : 'in voting'}. Are you sure you want to proceed?`,
          header: 'Confirmation Required',
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            setTimeout(() => resolve(true), 500);  // 500ms delay
          },
          reject: () => {
            setTimeout(() => resolve(false), 500);  // 500ms delay

          }
        });
      } else {
        resolve(true);  // or resolve(false), depending on what you want to do when it's neither live nor in voting
      }
    });
  }


}
